<template>
  <div class="list" v-if="counter !== 0">

    <!-- Capture -->
    <div class="list-header">

      <!-- Capture title -->
      <div class="list-header-title" v-if="subcategories.length">{{ getCurrentSlug ? naming : $t('Categories') }}</div>
      <h1 class="list-header-title" v-if="subcategories.length">{{ getCurrentSlug ? '' : currentPartnerName }}</h1>
      <!---->

      <!-- Preloader -->
      <h1 class="my15" v-else>
        <article-preloader />
      </h1>
      <!---->

    </div>
    <!---->

    <!-- Grid block -->
    <div class="list-wrapper" v-if="subcategories.length">
      <router-link class="list-item no-underline"
                   v-for="item in subcategories"
                   v-if="item.entity_id !== currentCategory.entity_id"
                   :key="item.entity_id"
                   :to="categoryLink(item)"
      >
        <div class="list-item-details">

          <!-- Article -->
          <div class="list-item-details-article">
            <h3 class="list-item-details-article-title"> {{ item.name }} </h3>
            <span class="list-item-details-article-count"> {{ item.product_count }} {{ $t('products') }} </span>
          </div>
          <!---->

          <!-- Photo -->
          <div class="list-item-details-photo relative" v-if="item.image">
            <images-lazy-load :image="{ src: categoryLogo(item.image) }" :alt="item.name" :height="imageHeight" :width="imageWidth" />
          </div>
          <!---->

        </div>
        <span class="list-item-link"> {{ $t('View') }} </span>
      </router-link>
    </div>
    <!---->

    <!-- Preloader -->
    <div class="list-wrapper" v-else>
      <grid-category-preloader v-for="item in counter" :key="item.id" />
    </div>
    <!---->

  </div>
</template>

<script async>
import config from 'config';
import { mapGetters } from 'vuex';
import { s3Path } from '@vue-storefront/core/helpers';
import { formatCategoryLink } from '@vue-storefront/core/modules/url/helpers';

const GridCategoryPreloader = () => import(/* webpackChunkName: "vsf-grid-category-preloader" */ 'theme/components/theme/blocks/Home/Preloaders/GridCategoryPreloader');
const ArticlePreloader = () => import(/* webpackChunkName: "vsf-article-preloader" */ 'theme/components/theme/blocks/Home/Preloaders/ArticlePreloader');
const ImagesLazyLoad = () => import(/* webpackChunkName: "vsf-images-lazy-load" */ 'korzyna/components/core/ImagesLazyLoad.vue');

export default {
  name: 'CategoryGrid',
  components: {
    ImagesLazyLoad,
    GridCategoryPreloader,
    ArticlePreloader
  },
  props: {
    // Category id
    currentId: {
      type: Number,
      default: 0
    },

    // Category title
    naming: {
      type: String
    },

    // Count of children categories
    counter: {
      type: Number
    }
  },
  computed: {
    ...mapGetters({
      currentPartner: 'partners/getCurrentPartner',
      categories: 'category/getCategories',
      currentCategory: 'category/getCurrentCategory'
    }),
    currentPartnerCode () {
      return this.currentPartner.code
    },
    currentPartnerName () {
      return this.currentPartner.title
    },
    currentLocationCode () {
      return this.$route.params.locationCode
    },
    getCurrentSlug () {
      return this.$route.params.slug
    },
    imageHeight () {
      return config.images.partner_category.height
    },
    imageWidth () {
      return config.images.partner_category.width
    },
    subcategories () {
      let parentId = this.currentCategory ? this.currentCategory.entity_id : 0
      if (!parentId && this.currentPartner) {
        parentId = this.currentPartner.root_category_id
      }

      return this.categories.filter(item => item.parent_id === parentId)
    }
  },
  methods: {
    categoryLogo (path) {
      return s3Path('/catalog/category/' + path, config.images.partner_category.width, config.images.partner_category.height)
    },
    categoryLink (category) {
      return `/${this.currentLocationCode}/${this.currentPartnerCode}${formatCategoryLink(category)}`
    }
  }
}
</script>

<style lang="scss" scoped>
.list {
  width: 100%;
  margin-bottom: 30px;

  &-header {
    width: 100%;
    margin: 15px 0;

    &-title {
      margin: 0;
      display: inline-block;
      color: #111;
      font-size: 18px;
      font-weight: 600;
    }
  }

  &-wrapper {
    display: flex;
    flex-wrap: wrap;
  }

  &-item {
    width: calc(100% / 3 - 54px);
    height: 120px;
    margin-right: 20px;
    margin-bottom: 20px;
    padding: 20px;

    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;

    border-radius: 5px;
    box-shadow: 0 0 4px 2px rgba(0, 0, 0, .08);

    &:hover {
      cursor: pointer;

      &:before {
        opacity: 1;
        transform: scale(1.1);
      }
    }

    &:before {
      width: 160px;
      height: 160px;
      margin: auto;

      position: absolute;
      top: 0;
      right: -60px;
      bottom: 0;
      content: '';

      opacity: 0;
      background: #11bb2e;
      border-radius: 50%;
      transition: .2s;
      z-index: -1;

      @media only screen and (max-width: 1200px) {
        right: -90px;
      }

      @media only screen and (max-width: 991px) {
        right: -60px;
      }
    }

    @media only screen and (max-width: 768px) {
      width: calc(100% / 2 - 50px);

      &:nth-child(even) {
        margin-right: 0;
      }
    }

    @media only screen and (min-width: 769px) {
      &:nth-child(3n) {
        margin-right: 0;
      }
    }

    @media only screen and (max-width: 465px) {
      width: 100%;
      height: 80px;
      margin-right: 0;
    }

    &-details {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      &-article {

        &-title {
          margin: 0;

          color: #111;
          font-size: 16px;
          font-weight: 600;
        }

        &-count {
          margin-top: 10px;

          color: #111;
          font-size: 14px;
          font-weight: 400;
        }
      }

      &-photo {
        width: 75px;
        height: 75px;

        & img {
          width: 75px;
          height: 75px;
        }
      }
    }

    &-link {
      color: #11bb2e;
      font-size: 14px;
      font-weight: 400;

      &:hover {
        cursor: pointer;
        text-decoration-color: #11bb2e;
        text-decoration: underline;
      }
    }
  }
}
</style>
